import { ComponentProps } from '@stitches/react'
import { Avatar } from 'common/Avatar'
import { Button } from 'common/Button'
import { Link } from 'common/Link'
import { AppRoutes } from 'config/routes'
import { FeedType } from 'graphql/types'
import { useTimeDistance } from 'hooks/useTimeDistance'
import { useToggleFollowProfile } from 'hooks/useToggleFollowProfile'
import { useStore } from 'lib/store'
import { theme } from 'lib/theme'
import { NSFW_EVENT } from 'lib/tracking/types'
import { observer } from 'mobx-react-lite'
import { PaginatedPosts } from 'models/PaginatedPosts'
import { Post } from 'models/Post'
import { Box } from 'primitives/Box'
import { Flex } from 'primitives/Flex'
import { Text } from 'primitives/Text'
import { MouseEventHandler, useState } from 'react'
import { PostCardMoreMenu } from '../../PostCardMoreMenu'
import { PostIconButton } from './IconButton'

export interface PostTopBarProps extends ComponentProps<typeof Flex> {
  post: Post
}

const _PostTopBar: React.FC<PostTopBarProps> = ({ css, post }) => {
  const store = useStore()

  const profilePath = `${AppRoutes.PROFILE}/${post.profile?.username}`

  const [moreMenuIsOpen, setMoreMenuIsOpen] = useState<boolean>()

  const { timeDistance } = useTimeDistance(post.publishedAt)

  const { toggleFollowProfile } = useToggleFollowProfile(post?.profile)

  const isFollowingFeed = store.feed.currentFeedType === FeedType.FOLLOWING

  const handleFollowProfile: MouseEventHandler<HTMLButtonElement> = (e) => {
    toggleFollowProfile(e).then(({ success, following }) => {
      if (success && following) {
        store.analytics.track(NSFW_EVENT.FEED_POST_PROFILE_FOLLOW, {
          feedType: post.feedType,
          page: post.feedPage,
          postId: post.postId,
          profileId: post.profile!.profileId,
          username: post.profile!.username
        })
      }
    })
  }

  const handleClickPostProfile = () => {
    store.analytics.track(NSFW_EVENT.FEED_POST_PROFILE_VIEW, {
      feedType: post.feedType,
      page: post.feedPage,
      postId: post.postId,
      profileId: post.profile!.profileId,
      username: post.profile!.username
    })
  }

  return (
    <Flex
      css={{
        display: 'flex',
        backgroundColor: theme.colors.gray300,
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 16px 0px 16px',
        '@lg': {
          borderTopLeftRadius: '6px',
          borderTopRightRadius: '6px',
          ...css?.['@lg']
        },
        ...css
      }}
    >
      <Flex css={{ alignItems: 'center' }}>
        <Link nounderline href={profilePath} onClick={handleClickPostProfile}>
          <Avatar
            src={post?.profile?.avatar?.url}
            username={post.profile?.username}
            size="small"
            css={{ cursor: 'pointer' }}
          />
        </Link>
        <Box css={{ marginLeft: '8px' }}>
          <Link
            nounderline
            href={profilePath}
            onClick={handleClickPostProfile}
            css={{ display: 'block' }}
          >
            <Text type="body3" medium css={{ lineHeight: '16px', textDecoration: 'none' }}>
              {post.profile?.displayName || 'user'}
            </Text>
          </Link>
          <Link nounderline href={profilePath} onClick={handleClickPostProfile}>
            <Text
              css={{
                fontSize: '12px',
                lineHeight: '16px',
                color: theme.colors.gray800
              }}
            >
              {timeDistance}
            </Text>
          </Link>
        </Box>
      </Flex>
      <Flex css={{ alignItems: 'center' }}>
        {!post.isOwner && !isFollowingFeed && post.parentFeed instanceof PaginatedPosts && (
          <Button follow onClick={handleFollowProfile}>
            {post.profile?.followingStatus}
          </Button>
        )}
        {store.user.account && (
          <PostIconButton buttonIcon="More" onClick={() => setMoreMenuIsOpen(true)} />
        )}
      </Flex>

      <PostCardMoreMenu
        isOpen={moreMenuIsOpen}
        onIgnore={() => setMoreMenuIsOpen(false)}
        post={post}
      />
    </Flex>
  )
}

export const PostTopBar = observer(_PostTopBar)
